import type { EducationSystemDefinition } from './educationSystemDefinitions';

export const TENANT_CUSTOM_PREFIX = 'tenant-custom:';

export function isTenantCustomEducationSystemKey(educationSystemKey: string): boolean {
  return educationSystemKey.trim().toLowerCase().startsWith(TENANT_CUSTOM_PREFIX);
}

/** Guid string after `tenant-custom:` or null. */
export function tenantCustomSystemIdFromKey(educationSystemKey: string): string | null {
  const t = educationSystemKey.trim();
  if (!t.toLowerCase().startsWith(TENANT_CUSTOM_PREFIX)) return null;
  const id = t.slice(TENANT_CUSTOM_PREFIX.length).trim();
  return id || null;
}

export type C314BandPayload = {
  C314_KeyStageId: string;
  C314_Name: string;
  C314_Years: Array<{ C314_YearId: string; C314_YearName: string }>;
};

export type EnsurePlacementResult =
  | { ok: true; needsSave: boolean; keyStageId: string; yearId: string; bands: C314BandPayload[] }
  | { ok: false; message: string };

export type C314KeyStageOnlyResult =
  | { ok: true; needsSave: boolean; keyStageId: string; bands: C314BandPayload[] }
  | { ok: false; message: string };

export type C314YearOnlyResult =
  | { ok: true; needsSave: boolean; keyStageId: string; yearId: string; bands: C314BandPayload[] }
  | { ok: false; message: string };

function findKeyStageNameCaseInsensitive(def: EducationSystemDefinition, keyStageName: string) {
  const want = keyStageName.trim().toLowerCase();
  return def.keyStages.find((k) => k.name.trim().toLowerCase() === want);
}

/** Server (SaveCustomSystemStructure) enforces this message shape — keep in sync. */
export function yearLabelDuplicateInEducationSystemMessage(yearLabel: string): string {
  const y = yearLabel.trim();
  return `Year label "${y}" is already used in this education system. Year labels must be unique across all key stages.`;
}

/** True if `yLower` is used as a year label on any C314 band other than `excludeBandIndex`. */
function isYearLabelUsedOnOtherBand(
  bands: C314BandPayload[],
  yLower: string,
  excludeBandIndex: number
): boolean {
  for (let i = 0; i < bands.length; i++) {
    if (i === excludeBandIndex) continue;
    if (bands[i].C314_Years.some((y) => y.C314_YearName.trim().toLowerCase() === yLower)) {
      return true;
    }
  }
  return false;
}

/**
 * Full C314 band list from the current E313 definition (stable ids required).
 */
export function educationDefinitionToC314Bands(
  def: EducationSystemDefinition
): { ok: true; bands: C314BandPayload[] } | { ok: false; message: string } {
  const bands: C314BandPayload[] = [];

  for (const ks of def.keyStages) {
    const ksId = ks.keyStageId?.trim();
    if (!ksId) {
      return {
        ok: false,
        message: `Key stage "${ks.name}" is missing a stable id. Open Control Panel → Education system and save the structure.`
      };
    }
    if (!ks.years?.length) {
      bands.push({ C314_KeyStageId: ksId, C314_Name: ks.name, C314_Years: [] });
      continue;
    }

    const years: C314BandPayload['C314_Years'] = [];
    for (const y of ks.years) {
      const id = y.yearId?.trim();
      if (!id) {
        return {
          ok: false,
          message: `Year "${y.yearName}" under "${ks.name}" has no stable id. Fix it in Control Panel → Education system.`
        };
      }
      years.push({ C314_YearId: id, C314_YearName: y.yearName.trim() });
    }
    bands.push({ C314_KeyStageId: ksId, C314_Name: ks.name, C314_Years: years });
  }

  return { ok: true, bands };
}

/** Persist a new key stage with no years yet (tenant custom dialogs). */
export function buildC314BandsAddingKeyStageOnly(
  def: EducationSystemDefinition,
  keyStageName: string,
  newGuid: () => string
): C314KeyStageOnlyResult {
  const ksTrim = keyStageName.trim();
  if (!ksTrim) {
    return { ok: false, message: 'Key stage name is required.' };
  }

  const base = educationDefinitionToC314Bands(def);
  if (!base.ok) return base;

  const bands = base.bands;
  const dup = bands.findIndex((b) => b.C314_Name.trim().toLowerCase() === ksTrim.toLowerCase());
  if (dup >= 0) {
    return { ok: true, needsSave: false, keyStageId: bands[dup].C314_KeyStageId, bands };
  }

  const newKsId = newGuid();
  bands.push({ C314_KeyStageId: newKsId, C314_Name: ksTrim, C314_Years: [] });
  return { ok: true, needsSave: true, keyStageId: newKsId, bands };
}

/** Persist a new year under an existing key stage. */
export function buildC314BandsAddingYearOnly(
  def: EducationSystemDefinition,
  keyStageName: string,
  yearLabel: string,
  newGuid: () => string
): C314YearOnlyResult {
  const ksTrim = keyStageName.trim();
  const yTrim = yearLabel.trim();
  if (!ksTrim || !yTrim) {
    return { ok: false, message: 'Key stage and year are required.' };
  }

  const base = educationDefinitionToC314Bands(def);
  if (!base.ok) return base;

  const bands = base.bands;
  const yLower = yTrim.toLowerCase();
  const bandIdx = bands.findIndex((b) => b.C314_Name.trim().toLowerCase() === ksTrim.toLowerCase());
  if (bandIdx < 0) {
    return { ok: false, message: 'Key stage not found. Add the key stage first, then add a year.' };
  }

  const band = bands[bandIdx];
  const yearHit = band.C314_Years.find((y) => y.C314_YearName.trim().toLowerCase() === yLower);
  if (yearHit) {
    return {
      ok: true,
      needsSave: false,
      keyStageId: band.C314_KeyStageId,
      yearId: yearHit.C314_YearId,
      bands
    };
  }

  if (isYearLabelUsedOnOtherBand(bands, yLower, bandIdx)) {
    return { ok: false, message: yearLabelDuplicateInEducationSystemMessage(yTrim) };
  }

  const yId = newGuid();
  const nextBand: C314BandPayload = {
    ...band,
    C314_Years: [...band.C314_Years, { C314_YearId: yId, C314_YearName: yTrim }]
  };
  bands[bandIdx] = nextBand;
  return {
    ok: true,
    needsSave: true,
    keyStageId: band.C314_KeyStageId,
    yearId: yId,
    bands
  };
}

/**
 * Builds a full C314 payload when a new key stage and/or year must be persisted on a tenant custom system.
 * If the year already exists under the key stage, returns needsSave: false and the existing ids.
 */
export function buildC314BandsEnsuringPlacement(
  def: EducationSystemDefinition,
  keyStageName: string,
  yearLabel: string,
  newGuid: () => string
): EnsurePlacementResult {
  const ksTrim = keyStageName.trim();
  const yTrim = yearLabel.trim();
  if (!ksTrim || !yTrim) {
    return { ok: false, message: 'Key stage and year are required.' };
  }

  const base = educationDefinitionToC314Bands(def);
  if (!base.ok) return base;

  const bands = base.bands;

  const yLower = yTrim.toLowerCase();
  const ksDefHit = findKeyStageNameCaseInsensitive(def, ksTrim);
  const bandIdx = bands.findIndex((b) => b.C314_Name.trim().toLowerCase() === ksTrim.toLowerCase());

  if (ksDefHit && bandIdx >= 0) {
    const band = bands[bandIdx];
    const yearHit = band.C314_Years.find((y) => y.C314_YearName.trim().toLowerCase() === yLower);
    if (yearHit) {
      return {
        ok: true,
        needsSave: false,
        keyStageId: band.C314_KeyStageId,
        yearId: yearHit.C314_YearId,
        bands
      };
    }

    if (isYearLabelUsedOnOtherBand(bands, yLower, bandIdx)) {
      return { ok: false, message: yearLabelDuplicateInEducationSystemMessage(yTrim) };
    }

    const yId = newGuid();
    const nextBand: C314BandPayload = {
      ...band,
      C314_Years: [...band.C314_Years, { C314_YearId: yId, C314_YearName: yTrim }]
    };
    bands[bandIdx] = nextBand;
    return {
      ok: true,
      needsSave: true,
      keyStageId: band.C314_KeyStageId,
      yearId: yId,
      bands
    };
  }

  if (bands.some((b) => b.C314_Years.some((y) => y.C314_YearName.trim().toLowerCase() === yLower))) {
    return { ok: false, message: yearLabelDuplicateInEducationSystemMessage(yTrim) };
  }

  const newKsId = newGuid();
  const newYId = newGuid();
  bands.push({
    C314_KeyStageId: newKsId,
    C314_Name: ksTrim,
    C314_Years: [{ C314_YearId: newYId, C314_YearName: yTrim }]
  });

  return {
    ok: true,
    needsSave: true,
    keyStageId: newKsId,
    yearId: newYId,
    bands
  };
}
